import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  X, 
  CheckCircle2, 
  XCircle, 
  Zap, 
  Flag, 
  Heart,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  AlertCircle
} from 'lucide-react';
import { useFavorites } from '../../hooks/useFavorites';
import { normalizeCorrectAnswer } from '../../utils/answerNormalization';

interface ExamInterfaceProps {
  questionsBySubject: Record<string, any[]>;
  subjectsData: any[];
  duration: number;
  negativeMarking: boolean;
  onCancel: () => void;
  isDarkMode?: boolean;
  chapterName?: string;
  questionType?: 'mcq' | 'cq';
  title?: string;
}

// ==========================================
// 1. PERFORMANCE WRAPPERS & LAZY LOAD CARDS
// ==========================================

const LazyLoadCard = ({ children, height = 180 }: { children: React.ReactNode, height?: number }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Load cards slightly before they enter the viewport
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="w-full" style={{ minHeight: isIntersecting ? 'auto' : `${height}px` }}>
      {isIntersecting ? children : (
        <div className="w-full bg-[#f8fafc] dark:bg-white/5 animate-pulse rounded-3xl" style={{ height: `${height}px` }} />
      )}
    </div>
  );
};

// ==========================================
// 2. MEMOIZED ACTIVE QUESTION CARDS
// ==========================================

interface MCQQuestionItemProps {
  question: any;
  index: number;
  selectedOption: number | undefined;
  onOptionSelect: (qId: number, optIdx: number) => void;
  isFavorite: boolean;
  onToggleFavorite: (q: any) => void;
  toBengaliNumber: (n: number | string) => string;
  getQuestionReference: (q: any) => string | null;
  isFlagged?: boolean;
  onReport?: (qId: number) => void;
}

const MCQQuestionItem = React.memo(({
  question,
  index,
  selectedOption,
  onOptionSelect,
  isFavorite,
  onToggleFavorite,
  toBengaliNumber,
  getQuestionReference,
  isFlagged = false,
  onReport
}: MCQQuestionItemProps) => {
  return (
    <div className="p-6 bg-[#ffffff] dark:bg-[#1E1E1E]/60 border border-gray-100 dark:border-white/5 rounded-3xl text-left space-y-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] dark:shadow-none">
      <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold relative flex-shrink-0">
            {toBengaliNumber(index + 1)}
            <div className="w-2 h-2 rounded-full bg-[#00CBA9] absolute -top-0.5 -right-0.5 border border-white dark:border-[#1E1E1E]" />
          </div>
          <div className="flex-1 space-y-1.5">
            <h3 className="text-base sm:font-semibold font-normal text-slate-900 dark:text-slate-100 leading-relaxed font-bangla whitespace-pre-line">
              {question.question_text}
            </h3>
            {getQuestionReference(question) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 font-bangla font-medium">
                {getQuestionReference(question)}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-1 flex-shrink-0 self-end sm:self-auto">
          <button 
            type="button" 
            onClick={() => onReport?.(question.id)}
            className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${isFlagged ? 'text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20' : 'text-gray-400 dark:text-gray-500 hover:text-[#00CBA9] hover:bg-teal-50 dark:hover:bg-teal-950/20'}`}
            title="রিপোর্ট করুন"
          >
            <Flag className={`w-4 h-4 ${isFlagged ? 'text-[#00CBA9] fill-[#00CBA9]' : ''}`} />
          </button>
          <button 
            type="button"
            onClick={() => onToggleFavorite(question)}
            className="p-1.5 transition-colors cursor-pointer bg-transparent border-none"
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'text-rose-500 fill-rose-500' : 'text-gray-400 dark:text-gray-500 hover:text-rose-500'}`} />
          </button>
        </div>
      </div>

      {question.stem && (
        <div className="p-4 rounded-2xl bg-[#F4F7FB] dark:bg-white/5 border-l-4 border-l-blue-500 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-bangla whitespace-pre-line ml-0 md:ml-12">
          <strong className="block text-slate-550 dark:text-slate-400 mb-1">উদ্দীপক:</strong>
          {question.stem}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 pl-0 md:pl-12">
        {question.options && question.options.map((option: string, optIdx: number) => {
          const isSelected = selectedOption === optIdx;
          
          return (
            <button
              key={optIdx}
              type="button"
              onClick={() => onOptionSelect(question.id, optIdx)}
              className={`group flex items-center p-3.5 rounded-2xl border transition-all text-left w-full cursor-pointer ${
                isSelected
                  ? 'border-blue-500 bg-blue-500/5 text-blue-650 dark:text-blue-300 shadow-sm'
                  : 'border-gray-200 dark:border-white/10 bg-[#ffffff] dark:bg-white/5 hover:border-blue-500/30 dark:hover:border-blue-500/25 hover:bg-blue-50/5 dark:hover:bg-blue-500/5 hover:shadow-sm text-slate-700 dark:text-slate-300'
              }`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bangla text-xs font-bold mr-3.5 flex-shrink-0 transition-colors ${
                isSelected
                  ? 'bg-blue-500 text-white shadow-md' 
                  : 'bg-gray-200 dark:bg-slate-800 text-slate-400 dark:text-slate-400 group-hover:bg-slate-300 dark:group-hover:bg-slate-700 group-hover:text-slate-800 dark:group-hover:text-slate-200'
              }`}>
                {['ক', 'খ', 'গ', 'ঘ'][optIdx]}
              </span>
              <span className="text-sm sm:font-medium font-normal leading-relaxed font-bangla">
                {option}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
});
MCQQuestionItem.displayName = 'MCQQuestionItem';

interface CQQuestionItemProps {
  question: any;
  index: number;
  expandedStates: Record<string, boolean>;
  onToggleSubQuestion: (qId: number, label: string) => void;
  toBengaliNumber: (n: number | string) => string;
  getQuestionReference: (q: any) => string | null;
}

const CQQuestionItem = React.memo(({
  question,
  index,
  expandedStates,
  onToggleSubQuestion,
  toBengaliNumber,
  getQuestionReference
}: CQQuestionItemProps) => {
  const parsedCQ = typeof question.question_text === 'object' && question.question_text !== null
    ? question.question_text
    : (() => {
        try { return JSON.parse(question.question_text); } catch (e) { return {}; }
      })();

  return (
    <div className="p-6 bg-[#ffffff] dark:bg-[#1E1E1E]/60 border border-gray-100 dark:border-white/5 rounded-3xl text-left space-y-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] dark:shadow-none">
      <div className="flex items-start gap-4">
        <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold flex-shrink-0">
          {toBengaliNumber(index + 1)}
        </div>
        <div className="flex-1 space-y-1.5">
          <h3 className="text-base sm:font-semibold font-normal text-slate-900 dark:text-slate-100 leading-relaxed font-bangla">
            সৃজনশীল প্রশ্ন {toBengaliNumber(index + 1)}
          </h3>
          {getQuestionReference(question) && (
            <p className="text-xs text-gray-500 dark:text-gray-400 font-bangla font-medium">
              {getQuestionReference(question)}
            </p>
          )}
        </div>
      </div>

      {question.stem && (
        <div className="p-4 rounded-2xl bg-[#F4F7FB] dark:bg-white/5 border-l-4 border-l-blue-500 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-bangla whitespace-pre-line ml-0 md:ml-12">
          <strong className="block text-slate-550 dark:text-slate-400 mb-1">উদ্দীপক:</strong>
          {question.stem}
        </div>
      )}

      <div className="space-y-4 pl-4 md:pl-12">
        {['ক', 'খ', 'গ', 'ঘ'].map((label) => {
          const subQ = parsedCQ?.[label];
          if (!subQ) return null;
          const isExpanded = expandedStates[`${question.id}-${label}`];

          return (
            <div key={label} className="space-y-2 border-l-2 border-gray-100 dark:border-white/5 pl-4 relative">
              <div className="absolute -left-2.5 top-0.5 w-5 h-5 rounded-full bg-[#F4F7FB] dark:bg-white/5 border border-gray-200 dark:border-white/10 flex items-center justify-center font-bangla text-[10px] font-bold text-[#00CBA9]">
                {label}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                <p className="font-bangla text-sm text-[#1F1F1F] dark:text-[#E8EAED] leading-relaxed">
                  {subQ.text} <span className="text-xs text-gray-400 dark:text-gray-500 font-semibold font-bangla ml-1">(মান: {subQ.marks})</span>
                </p>
                <button
                  type="button"
                  onClick={() => onToggleSubQuestion(question.id, label)}
                  className="text-xs text-[#00CBA9] font-bangla font-semibold hover:underline bg-transparent border-none cursor-pointer flex-shrink-0 self-start sm:self-center"
                >
                  {isExpanded ? 'উত্তর লুকান' : 'উত্তর দেখুন'}
                </button>
              </div>
              {isExpanded && subQ.sub_question_answer && (
                <div className="p-4 rounded-2xl bg-amber-500/5 border border-amber-500/10 text-xs text-amber-700 dark:text-amber-300 font-bangla leading-relaxed animate-fadeIn">
                  <strong>নমুনা উত্তর:</strong> {subQ.sub_question_answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
CQQuestionItem.displayName = 'CQQuestionItem';

// ==========================================
// 3. MEMOIZED POST-EXAM REVIEW CARDS
// ==========================================

interface MCQReviewItemProps {
  question: any;
  index: number;
  selectedOption: number | undefined;
  correctOption: number;
  isFavorite: boolean;
  onToggleFavorite: (q: any) => void;
  toBengaliNumber: (n: number | string) => string;
  getQuestionReference: (q: any) => string | null;
  isFlagged?: boolean;
  onReport?: (qId: number) => void;
}

const MCQReviewItem = React.memo(({
  question,
  index,
  selectedOption,
  correctOption,
  isFavorite,
  onToggleFavorite,
  toBengaliNumber,
  getQuestionReference,
  isFlagged = false,
  onReport
}: MCQReviewItemProps) => {
  const isCorrect = selectedOption === correctOption;
  const isSkipped = selectedOption === undefined;

  return (
    <div className="p-6 bg-[#ffffff] dark:bg-[#1E1E1E]/60 border border-gray-100 dark:border-white/5 transition-all rounded-3xl text-left space-y-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)]">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 flex-1">
          <div className="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-[#8AB4F8] flex items-center justify-center font-english text-xs font-bold relative flex-shrink-0">
            {toBengaliNumber(index + 1)}
          </div>
          <div className="flex-1 space-y-1.5">
            <h4 className="text-base sm:font-semibold font-normal text-slate-900 dark:text-slate-100 leading-relaxed font-bangla whitespace-pre-line">
              {question.question_text}
            </h4>
            {getQuestionReference(question) && (
              <p className="text-xs text-gray-500 dark:text-gray-400 font-bangla font-medium">
                {getQuestionReference(question)}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0 self-end sm:self-auto">
          {isSkipped ? (
            <span className="text-xs bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400 px-3 py-1 rounded-full flex items-center gap-1.5 font-bangla font-medium">
              <AlertCircle className="w-3.5 h-3.5" />
              উত্তরিত নয়
            </span>
          ) : isCorrect ? (
            <span className="text-xs bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-full flex items-center gap-1.5 font-bangla font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" />
              সঠিক
            </span>
          ) : (
            <span className="text-xs bg-rose-500/10 text-rose-400 px-3 py-1 rounded-full flex items-center gap-1.5 font-bangla font-medium">
              <XCircle className="w-3.5 h-3.5" />
              ভুল
            </span>
          )}

          <div className="flex items-center gap-1 flex-shrink-0">
            <button 
              type="button" 
              onClick={() => onReport?.(question.id)}
              className={`p-1.5 rounded-xl transition-all duration-200 cursor-pointer bg-transparent border-none ${isFlagged ? 'text-[#00CBA9] bg-emerald-50 dark:bg-emerald-950/20' : 'text-gray-400 dark:text-gray-500 hover:text-[#00CBA9] hover:bg-teal-50 dark:hover:bg-teal-950/20'}`}
              title="রিপোর্ট করুন"
            >
              <Flag className={`w-4 h-4 ${isFlagged ? 'text-[#00CBA9] fill-[#00CBA9]' : ''}`} />
            </button>
            <button 
              type="button"
              onClick={() => onToggleFavorite(question)}
              className="p-1.5 transition-colors cursor-pointer bg-transparent border-none"
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'text-rose-500 fill-rose-500' : 'text-gray-400 dark:text-gray-500 hover:text-rose-500'}`} />
            </button>
          </div>
        </div>
      </div>

      {question.stem && (
        <div className="p-4 rounded-2xl bg-[#F4F7FB] dark:bg-white/5 border-l-4 border-l-blue-500 text-sm leading-relaxed text-slate-700 dark:text-slate-300 font-bangla whitespace-pre-line ml-0 md:ml-12">
          <strong className="block text-slate-550 dark:text-slate-400 mb-1">উদ্দীপক:</strong>
          {question.stem}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 pl-0 md:pl-12">
        {question.options && question.options.map((option: string, optIdx: number) => {
          const isSelected = selectedOption === optIdx;
          const isOptCorrect = optIdx === correctOption;

          let style = "border-slate-200 dark:border-slate-800 bg-[#f8fafc]/50 dark:bg-brand-dark-surface/50 text-slate-600 dark:text-slate-400";
          let bullet = "bg-gray-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400";

          if (isOptCorrect) {
            style = "border-emerald-500 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300";
            bullet = "bg-emerald-500 text-white";
          } else if (isSelected) {
            style = "border-rose-500 bg-rose-500/10 text-rose-600 dark:text-rose-300";
            bullet = "bg-rose-500 text-white";
          }

          return (
            <div 
              key={optIdx}
              className={`flex items-center p-3.5 rounded-2xl border transition-all text-left w-full ${style}`}
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bangla text-xs font-bold mr-3.5 flex-shrink-0 ${bullet}`}>
                {['ক', 'খ', 'গ', 'ঘ'][optIdx]}
              </span>
              <span className="text-sm sm:font-medium font-normal leading-relaxed font-bangla">{option}</span>
            </div>
          );
        })}
      </div>

      {question.explanation && (
        <div className="mt-4 p-4 rounded-xl bg-blue-50/50 dark:bg-blue-500/5 border border-blue-100 dark:border-blue-500/10 text-xs text-blue-600 dark:text-blue-300 leading-relaxed font-bangla ml-0 md:ml-12">
          <strong>ব্যাখ্যা:</strong> {question.explanation}
        </div>
      )}
    </div>
  );
});
MCQReviewItem.displayName = 'MCQReviewItem';

// ==========================================
// 4. MAIN EXAM ENGINE COMPONENT
// ==========================================

export default function ExamInterface({
  questionsBySubject,
  subjectsData,
  duration,
  negativeMarking,
  onCancel,
  chapterName,
  questionType = 'mcq',
  title = 'কুইক টেস্ট'
}: ExamInterfaceProps) {
  const shAuth = (window as any).shAuth;
  const [localQuestionsBySubject, setLocalQuestionsBySubject] = useState(questionsBySubject);
  const [isFetchingAnswers, setIsFetchingAnswers] = useState(false);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [activeQuestionIdx, setActiveQuestionIdx] = useState<number>(0);
  const [expandedSubQuestions, setExpandedSubQuestions] = useState<Record<string, boolean>>({});
  const [timeLeft, setTimeLeft] = useState<number>(duration * 60);
  const [isExamOver, setIsExamOver] = useState<boolean>(false);
  const [results, setResults] = useState<{
    score: number;
    correct: number;
    wrong: number;
    unanswered: number;
    percentage: number;
  } | null>(null);
  const [reviewFilter, setReviewFilter] = useState<'all' | 'correct' | 'wrong' | 'skipped'>('all');

  // Scroll spy & programmatic scroll tracking refs
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Active subject selection
  const subjectKeys = Object.keys(localQuestionsBySubject);
  const [activeSubject, setActiveSubject] = useState<string>(subjectKeys[0] || '');
  const activeQuestions = localQuestionsBySubject[activeSubject] || [];

  const { isFavorite, toggleFavorite } = useFavorites();

  const [flaggedQuestionIds, setFlaggedQuestionIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    const handleFlagged = (e: any) => {
      if (e.detail?.questionId) {
        setFlaggedQuestionIds(prev => {
          const next = new Set(prev);
          next.add(e.detail.questionId);
          return next;
        });
      }
    };
    window.addEventListener('question-flagged' as any, handleFlagged);
    return () => window.removeEventListener('question-flagged' as any, handleFlagged);
  }, []);

  // Synchronize prop updates to local state
  useEffect(() => {
    setLocalQuestionsBySubject(questionsBySubject);
    const keys = Object.keys(questionsBySubject);
    if (keys.length > 0) {
      setActiveSubject(prev => prev || keys[0]);
    }
  }, [questionsBySubject]);

  // Flatten all questions with subject info for progress and scoring
  const allQuestions = React.useMemo(() => {
    const list: any[] = [];
    Object.entries(localQuestionsBySubject).forEach(([subjId, qList]) => {
      qList.forEach((q) => {
        list.push({ ...q, subjectId: subjId });
      });
    });
    return list;
  }, [localQuestionsBySubject]);

  const totalQuestionsCount = allQuestions.length;

  // Programmatic smooth scroll to subject section
  const scrollToSubject = (subjId: string) => {
    isScrollingRef.current = true;
    setActiveSubject(subjId);
    const element = document.getElementById(`subject-sec-${subjId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 800);
  };

  // Scroll listener to update active tab based on scroll offset (Scroll-Spy)
  const handleScroll = () => {
    if (isScrollingRef.current || !scrollContainerRef.current) return;
    const sections = subjectKeys.map(subjId => document.getElementById(`subject-sec-${subjId}`));
    const containerTop = scrollContainerRef.current.getBoundingClientRect().top;
    
    let currentActive = activeSubject;
    for (const section of sections) {
      if (section) {
        const rect = section.getBoundingClientRect();
        // A section is active if its top border is close to the top or above it
        if (rect.top - containerTop <= 120) {
          currentActive = section.id.replace('subject-sec-', '');
        }
      }
    }
    
    if (currentActive !== activeSubject) {
      setActiveSubject(currentActive);
    }
  };

  // Helper to check if a subject is fully answered
  const isSubjectCompleted = (subjId: string): boolean => {
    const questions = localQuestionsBySubject[subjId] || [];
    if (questions.length === 0) return false;
    return questions.every(q => answers[q.id] !== undefined);
  };

  // Auto-advance to next subject when current subject's questions are completed
  useEffect(() => {
    if (isExamOver || !activeSubject) return;

    const isCurrentCompleted = isSubjectCompleted(activeSubject);
    if (isCurrentCompleted) {
      const currentIndex = subjectKeys.indexOf(activeSubject);
      if (currentIndex !== -1 && currentIndex < subjectKeys.length - 1) {
        const nextSubject = subjectKeys[currentIndex + 1];
        if (!isSubjectCompleted(nextSubject)) {
          setTimeout(() => {
            scrollToSubject(nextSubject);
          }, 600); // minor delay for nice user transition feedback
        }
      }
    }
  }, [answers, activeSubject, isExamOver]);

  // Sync index boundary when active subject changes
  useEffect(() => {
    setActiveQuestionIdx(0);
  }, [activeSubject]);

  const toggleSubQuestion = (qId: number, label: string) => {
    const key = `${qId}-${label}`;
    setExpandedSubQuestions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Subject details mapping helper
  const getSubjectName = (subjId: string) => {
    const found = subjectsData.find((s) => s.id === subjId);
    return found ? found.name : subjId;
  };

  const getQuestionReference = React.useCallback((question: any): string | null => {
    let refs = question.exam_references;
    if (typeof refs === 'string') {
      try {
        refs = JSON.parse(refs);
      } catch (e) {
        refs = null;
      }
    }
    
    if (Array.isArray(refs) && refs.length > 0) {
      const formattedRefs = refs.map((ref: any) => {
        const parts: string[] = [];
        if (ref.reference_school) {
          parts.push(ref.reference_school);
        } else if (ref.reference_board) {
          parts.push(ref.reference_board);
        }
        if (ref.reference_year) {
          parts.push(ref.reference_year);
        }
        return parts.join(' - ');
      });
      return `[${formattedRefs.join(', ')}]`;
    }
    
    if (question.exam_year) {
      let years = question.exam_year;
      if (typeof years === 'string' && (years.startsWith('[') || years.startsWith('{'))) {
        try {
          const parsed = JSON.parse(years);
          years = Array.isArray(parsed) ? parsed.join(', ') : years;
        } catch (e) {}
      }
      return `[${years}]`;
    }
    
    return null;
  }, []);

  // Hide MobileHeader while exam is active
  useEffect(() => {
    document.body.classList.add('exam-active');
    return () => {
      document.body.classList.remove('exam-active');
    };
  }, []);

  // Timer Countdown Effect
  useEffect(() => {
    if (isExamOver) return;

    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isExamOver]);

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Memoized answer selector callback (no parent rerenders outside specific state changes)
  const handleOptionSelect = React.useCallback((qId: number, optionIdx: number) => {
    setAnswers((prev) => ({
      ...prev,
      [qId]: optionIdx
    }));
  }, []);

  const calculateScore = (mergedQuestions?: any[]) => {
    const questionsList = mergedQuestions || allQuestions;
    if (questionType === 'cq') {
      return {
        score: 0,
        correct: answeredCount,
        wrong: 0,
        unanswered: totalQuestionsCount - answeredCount,
        percentage: totalQuestionsCount > 0 ? Math.round((answeredCount / totalQuestionsCount) * 100) : 0
      };
    }

    let correct = 0;
    let wrong = 0;
    let unanswered = 0;

    questionsList.forEach((q) => {
      const selected = answers[q.id];
      if (selected === undefined) {
        unanswered++;
      } else {
        const correctOpt = normalizeCorrectAnswer(q.correct_answer);
        if (selected === correctOpt) {
          correct++;
        } else {
          wrong++;
        }
      }
    });

    const markPerCorrect = 1;
    const penaltyPerWrong = negativeMarking ? 0.25 : 0;
    const rawScore = (correct * markPerCorrect) - (wrong * penaltyPerWrong);
    const score = parseFloat(rawScore.toFixed(2));
    const percentage = totalQuestionsCount > 0 ? Math.max(0, Math.round((score / totalQuestionsCount) * 100)) : 0;

    return {
      score,
      correct,
      wrong,
      unanswered,
      percentage
    };
  };

  const submitExamAndFetchAnswers = async (isAutoSubmit: boolean = false) => {
    setIsFetchingAnswers(true);
    let mergedQuestions = allQuestions;
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
    if (shAuth?.rest_nonce) {
      requestHeaders['X-WP-Nonce'] = shAuth.rest_nonce;
    }
    try {
      const response = await fetch('/wp-json/shuddhoshikha/v1/questions/answers', {
        method: 'POST',
        headers: requestHeaders,
        body: JSON.stringify({ ids: allQuestions.map(q => q.id) })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.answers) {
          mergedQuestions = allQuestions.map(q => {
            const ansInfo = result.answers[q.id];
            if (ansInfo) {
              return {
                ...q,
                correct_answer: ansInfo.correct_answer || ansInfo.correct_option || '0',
                explanation: ansInfo.explanation || '',
                ক: ansInfo.ক || q.ক,
                খ: ansInfo.খ || q.খ,
                গ: ansInfo.গ || q.গ,
                ঘ: ansInfo.ঘ || q.ঘ,
                hints: ansInfo.hints || q.hints,
                answers: ansInfo.answers || q.answers
              };
            }
            return q;
          });
          
          setLocalQuestionsBySubject(prev => {
            const updated = { ...prev };
            Object.keys(updated).forEach(subjId => {
              updated[subjId] = updated[subjId].map(q => {
                const ansInfo = result.answers[q.id];
                if (ansInfo) {
                  return {
                    ...q,
                    correct_answer: ansInfo.correct_answer || ansInfo.correct_option || '0',
                    explanation: ansInfo.explanation || '',
                    ক: ansInfo.ক || q.ক,
                    খ: ansInfo.খ || q.খ,
                    গ: ansInfo.গ || q.গ,
                    ঘ: ansInfo.ঘ || q.ঘ,
                    hints: ansInfo.hints || q.hints,
                    answers: ansInfo.answers || q.answers
                  };
                }
                return q;
              });
            });
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Error fetching answers key:', error);
    } finally {
      setIsFetchingAnswers(false);
      const finalResults = calculateScore(mergedQuestions);
      setResults(finalResults);
      setIsExamOver(true);
      if (timerRef.current) clearInterval(timerRef.current);
      if (isAutoSubmit) {
        alert('সময় শেষ! আপনার পরীক্ষাটি স্বয়ংক্রিয়ভাবে জমা দেওয়া হয়েছে।');
      }

      // Telemetry: Save attempt to the database in batch
      const mapFrontendSubjectToDb = (id: string): string => {
        const map: Record<string, string> = {
          'sub_bn1': 'bangla',
          'sub_bn2': 'bangla-2nd',
          'sub_eng1': 'english',
          'sub_eng2': 'english-2nd',
          'sub_math': 'general-math',
          'sub_ict': 'ict',
          'sub_religion': 'religion',
          'sub_phy': 'physics',
          'sub_chem': 'chemistry',
          'sub_bio': 'biology',
          'sub_hmath': 'higher-math',
          'sub_hist': 'history',
          'sub_geo': 'geography',
          'sub_civ': 'civics',
          'sub_gsci': 'general-science',
          'sub_acc': 'accounting',
          'sub_fin': 'finance',
          'sub_bus': 'business',
          'sub_eco': 'economics',
          'opt_agri': 'agriculture',
          'opt_home': 'home-science'
        };
        return map[id] || id;
      };

      const optionsLabels = ['ক', 'খ', 'গ', 'ঘ'];
      const answersLog = mergedQuestions.map((q) => {
        const selected = answers[q.id];
        const correctOpt = normalizeCorrectAnswer(q.correct_answer);
        const isCorrect = selected === correctOpt;
        return {
          question_id: q.id,
          chosen_option: selected !== undefined && selected !== null ? optionsLabels[selected] : null,
          correct_option: correctOpt !== undefined && correctOpt !== null ? optionsLabels[correctOpt] : null,
          is_correct: isCorrect,
          subject_category: q.subjectId ? mapFrontendSubjectToDb(q.subjectId) : 'general'
        };
      });

      const examId = 'exam_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
      const dbSubjectSlug = subjectKeys.map(mapFrontendSubjectToDb).join(',');

      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (shAuth?.rest_nonce) {
        headers['X-WP-Nonce'] = shAuth.rest_nonce;
      }

      fetch('/wp-json/shuddhoshikha/v1/sync-history', {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          action: 'submit_exam',
          exam_id: examId,
          subject_slug: dbSubjectSlug,
          total_questions: totalQuestionsCount,
          correct_count: finalResults.correct,
          wrong_count: finalResults.wrong,
          obtained_score: finalResults.score,
          answers: answersLog
        }),
        keepalive: true
      })
      .then(res => res.json())
      .then(resData => {
        console.log('[ExamInterface] Telemetry saved successfully:', resData);
      })
      .catch(err => console.error('[ExamInterface] Failed to save telemetry:', err));
    }
  };

  const handleSubmit = () => {
    submitExamAndFetchAnswers(false);
  };

  const handleAutoSubmit = () => {
    submitExamAndFetchAnswers(true);
  };

  const answeredCount = React.useMemo(() => {
    if (questionType === 'cq') {
      const qIds = new Set<number>();
      Object.keys(expandedSubQuestions).forEach((key) => {
        const [qIdStr] = key.split('-');
        const qId = parseInt(qIdStr);
        if (!isNaN(qId)) {
          qIds.add(qId);
        }
      });
      return qIds.size;
    }
    return Object.keys(answers).length;
  }, [answers, expandedSubQuestions, questionType]);

  // Helper to convert numbers to Bengali digits
  const toBengaliNumber = (num: number | string) => {
    const bengaliDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
    return num.toString().split('').map(digit => {
      const parsed = parseInt(digit);
      return isNaN(parsed) ? digit : bengaliDigits[parsed];
    }).join('');
  };

  // Helper to format time taken in Bengali
  const formatTimeTaken = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;

    if (min === 0) {
      return `${toBengaliNumber(sec)} সেকেন্ড`;
    }
    if (sec === 0) {
      return `${toBengaliNumber(min)} মিনিট`;
    }
    return `${toBengaliNumber(min)} মিনিট ${toBengaliNumber(sec)} সেকেন্ড`;
  };

  // Filtered questions for post-exam review list
  const filteredQuestions = React.useMemo(() => {
    return allQuestions.map((q, idx) => ({ ...q, originalIndex: idx })).filter((question) => {
      if (reviewFilter === 'all') return true;
      const selectedOpt = answers[question.id];
      const correctOpt = normalizeCorrectAnswer(question.correct_answer);
      if (reviewFilter === 'correct') return selectedOpt === correctOpt;
      if (reviewFilter === 'wrong') return selectedOpt !== undefined && selectedOpt !== correctOpt;
      if (reviewFilter === 'skipped') return selectedOpt === undefined;
      return true;
    });
  }, [allQuestions, answers, reviewFilter]);

  // Group filtered questions by subject
  const filteredQuestionsBySubject = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    filteredQuestions.forEach((q) => {
      if (!grouped[q.subjectId]) {
        grouped[q.subjectId] = [];
      }
      grouped[q.subjectId].push(q);
    });
    return grouped;
  }, [filteredQuestions]);

  // Render Result / Scorecard Screen
  if (isExamOver && results) {
    return (
      <div className="fixed inset-0 z-50 exam-interface-fullscreen bg-[#ffffff] dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100 p-6 md:p-12 font-bangla select-none transition-colors duration-200 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-8 relative">
          
          {/* Close button */}
          <button
            onClick={onCancel}
            className="absolute top-0 right-0 p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-white/5 transition-colors cursor-pointer"
            title="বন্ধ করুন"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Page header */}
          <div className="bg-[#ffffff] dark:bg-[#1E1E1E]/60 border border-gray-100 dark:border-white/5 rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-lg shadow-amber-500/10 flex-shrink-0">
                <Zap className="w-4 h-4 sm:w-5.5 sm:h-5.5 stroke-[2.5]" />
              </div>
              <div className="text-left">
                <h2 className="font-bangla text-sm sm:text-base font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{title}</h2>
                <p className="font-bangla text-[10px] sm:text-xs text-gray-400 dark:text-[#A0A5AD] mt-0.5">
                  {chapterName || getSubjectName(activeSubject)} ({toBengaliNumber(activeQuestions.length)}টি প্রশ্ন)
                </p>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/20 px-2 sm:px-3.5 py-0.5 sm:py-1.5 rounded-full text-amber-600 dark:text-amber-400 font-english text-[10px] sm:text-xs font-bold flex-shrink-0 flex items-center gap-1">
              <span>•</span>
              <span>{toBengaliNumber(results.correct)}/{toBengaliNumber(totalQuestionsCount)}</span>
            </div>
          </div>

          {/* Statistics Grid */}
          {questionType === 'cq' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-6 bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200 dark:border-slate-800/50 rounded-2xl text-center space-y-1 transition-colors duration-200">
                <span className="text-2xl font-bold font-english text-blue-600 dark:text-blue-400">{toBengaliNumber(totalQuestionsCount)}</span>
                <p className="text-xs text-slate-550 dark:text-slate-400">মোট সৃজনশীল প্রশ্ন</p>
              </div>
              <div className="p-6 bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200 dark:border-slate-800/50 rounded-2xl text-center space-y-1 transition-colors duration-200">
                <span className="text-2xl font-bold font-english text-emerald-600 dark:text-emerald-400">{toBengaliNumber(results.correct)}</span>
                <p className="text-xs text-slate-550 dark:text-slate-400">উত্তর যাচাইকৃত</p>
              </div>
              <div className="p-6 bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200 dark:border-slate-800/50 rounded-2xl text-center space-y-1 transition-colors duration-200">
                <span className="text-2xl font-bold font-english text-slate-600 dark:text-slate-400">{toBengaliNumber(results.unanswered)}</span>
                <p className="text-xs text-slate-550 dark:text-slate-400">যাচাই করা হয়নি</p>
              </div>
            </div>
          ) : (
            <div className="p-6 bg-[#f8fafc] dark:bg-[#1E1E1E]/60 border border-slate-200 dark:border-white/5 rounded-3xl space-y-6 transition-colors duration-200">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white text-left">ফলাফল পর্যালোচনা</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                <div className="bg-[#ffffff] dark:bg-[#252525] border border-emerald-500/20 rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(16,185,129,0.05)] flex flex-col">
                  <div className="bg-emerald-500 text-white text-xs font-bold py-2 px-4 text-center font-bangla tracking-wide">
                    অর্জিত নম্বর
                  </div>
                  <div className="p-4 sm:p-6 flex-1 flex items-center justify-center gap-3">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4.5 h-4.5 sm:w-5.5 sm:h-5.5" />
                    </div>
                    <span className="text-xl sm:text-3xl font-extrabold font-english text-slate-800 dark:text-white">
                      {toBengaliNumber(results.score)} / {toBengaliNumber(totalQuestionsCount)}
                    </span>
                  </div>
                </div>

                <div className="bg-[#ffffff] dark:bg-[#252525] border border-blue-500/20 rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(59,130,246,0.05)] flex flex-col">
                  <div className="bg-blue-500 text-white text-xs font-bold py-2 px-4 text-center font-bangla tracking-wide">
                    সময় নিয়েছো
                  </div>
                  <div className="p-4 sm:p-6 flex-1 flex items-center justify-center gap-3">
                    <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-blue-500/10 text-blue-500 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-4.5 h-4.5 sm:w-5.5 sm:h-5.5" />
                    </div>
                    <span className="text-sm sm:text-xl font-bold font-bangla text-slate-800 dark:text-white text-center leading-tight">
                      {formatTimeTaken(duration * 60 - timeLeft)}
                    </span>
                  </div>
                </div>

                <div className="bg-[#ffffff] dark:bg-[#252525] border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-[0_4px_12px_rgba(0,0,0,0.02)] flex flex-col p-4 items-center justify-center col-span-2 md:col-span-1">
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center flex-shrink-0">
                    <svg viewBox="0 0 100 100" className="w-20 h-20 sm:w-24 sm:h-24 transform -rotate-90">
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        className="stroke-slate-100 dark:stroke-slate-800 fill-none"
                        strokeWidth="8"
                      />
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        className="stroke-blue-500 fill-none transition-all duration-1000"
                        strokeWidth="8"
                        strokeDasharray="251.2"
                        strokeDashoffset={251.2 - (251.2 * results.percentage) / 100}
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute flex flex-col items-center justify-center">
                      <span className="text-lg sm:text-xl font-bold font-english text-slate-800 dark:text-white">{toBengaliNumber(results.percentage)}%</span>
                      <span className="text-[9px] font-bangla text-slate-400">সঠিকতা</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Review Filter Tabs */}
              <div className="pt-2 border-t border-slate-100 dark:border-white/5 flex flex-col items-center space-y-3">
                <span className="text-xs font-bangla text-gray-400 dark:text-gray-500">
                  নিচের বাটনে ক্লিক করে প্রশ্নগুলো ফিল্টার করুন:
                </span>
                <div className="flex flex-wrap items-center justify-center gap-2.5">
                  <button
                    onClick={() => setReviewFilter('all')}
                    className={`px-4 py-2 rounded-full text-xs font-bangla font-semibold flex items-center gap-2 cursor-pointer transition-all border ${
                      reviewFilter === 'all'
                        ? 'bg-slate-800 border-slate-800 text-white dark:bg-slate-700 dark:border-slate-700 dark:text-white shadow-sm font-bold'
                        : 'bg-[#ffffff] border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${reviewFilter === 'all' ? 'bg-white' : 'bg-slate-400'}`} />
                    সব প্রশ্ন ({toBengaliNumber(totalQuestionsCount)})
                  </button>

                  <button
                    onClick={() => setReviewFilter('correct')}
                    className={`px-4 py-2 rounded-full text-xs font-bangla font-semibold flex items-center gap-2 cursor-pointer transition-all border ${
                      reviewFilter === 'correct'
                        ? 'bg-emerald-500 border-emerald-500 text-white shadow-sm font-bold'
                        : 'bg-[#ffffff] border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${reviewFilter === 'correct' ? 'bg-white' : 'bg-emerald-500'}`} />
                    {toBengaliNumber(results.correct)} সঠিক
                  </button>

                  <button
                    onClick={() => setReviewFilter('skipped')}
                    className={`px-4 py-2 rounded-full text-xs font-bangla font-semibold flex items-center gap-2 cursor-pointer transition-all border ${
                      reviewFilter === 'skipped'
                        ? 'bg-amber-500 border-amber-500 text-white shadow-sm font-bold'
                        : 'bg-[#ffffff] border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${reviewFilter === 'skipped' ? 'bg-white' : 'bg-amber-500'}`} />
                    {toBengaliNumber(results.unanswered)} স্কিপ
                  </button>

                  <button
                    onClick={() => setReviewFilter('wrong')}
                    className={`px-4 py-2 rounded-full text-xs font-bangla font-semibold flex items-center gap-2 cursor-pointer transition-all border ${
                      reviewFilter === 'wrong'
                        ? 'bg-rose-500 border-rose-500 text-white shadow-sm font-bold'
                        : 'bg-[#ffffff] border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-white/5 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/10'
                    }`}
                  >
                    <span className={`w-2 h-2 rounded-full ${reviewFilter === 'wrong' ? 'bg-white' : 'bg-rose-500'}`} />
                    {toBengaliNumber(results.wrong)} ভুল
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Detailed Question Review List with lazy loading */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white text-left">প্রশ্নপত্র ও উত্তর পর্যালোচনা</h3>
            
            <div className="space-y-8">
              {filteredQuestions.length === 0 ? (
                <div className="py-12 text-center bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200 dark:border-slate-800/40 rounded-3xl">
                  <p className="font-bangla text-slate-550 dark:text-slate-400">এই ক্যাটাগরির কোনো প্রশ্ন পাওয়া যায়নি।</p>
                </div>
              ) : (
                Object.entries(filteredQuestionsBySubject).map(([subjId, questions]) => (
                  <div key={subjId} className="space-y-4">
                    {/* Subject Sub Header in Results */}
                    <div className="flex items-center gap-3 border-b border-gray-150 dark:border-white/5 pb-2 pt-4">
                      <div className="w-1.5 h-5 rounded bg-blue-500" />
                      <h4 className="font-bangla text-base font-bold text-slate-850 dark:text-white">
                        {getSubjectName(subjId)} ({toBengaliNumber(questions.length)}টি প্রশ্ন)
                      </h4>
                    </div>
                    <div className="space-y-6">
                      {questions.map((question) => {
                        const isCQ = question.type === 'cq' || questionType === 'cq';

                        if (isCQ) {
                          return (
                            <LazyLoadCard key={question.id} height={260}>
                              <CQQuestionItem 
                                question={question}
                                index={question.originalIndex}
                                expandedStates={expandedSubQuestions}
                                onToggleSubQuestion={toggleSubQuestion}
                                toBengaliNumber={toBengaliNumber}
                                getQuestionReference={getQuestionReference}
                              />
                            </LazyLoadCard>
                          );
                        }

                        const selectedOpt = answers[question.id];
                        const correctOpt = normalizeCorrectAnswer(question.correct_answer);

                        return (
                          <LazyLoadCard key={question.id} height={180}>
                            <MCQReviewItem
                              question={question}
                              index={question.originalIndex}
                              selectedOption={selectedOpt}
                              correctOption={correctOpt}
                              isFavorite={isFavorite(question.id)}
                              onToggleFavorite={(q) => toggleFavorite({
                                id: q.id,
                                question_text: q.question_text,
                                options: q.options,
                                correct_answer: q.correct_answer,
                                explanation: q.explanation,
                                stem: q.stem,
                                reference: q.reference,
                                subject_id: q.subjectId,
                                subject_name: subjectsData.find(s => s.id === q.subjectId)?.name
                              })}
                              toBengaliNumber={toBengaliNumber}
                              getQuestionReference={getQuestionReference}
                              isFlagged={flaggedQuestionIds.has(question.id)}
                              onReport={(qId) => (window as any).showReportModal?.(qId)}
                            />
                          </LazyLoadCard>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-center gap-4 pt-4">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-8 py-3.5 rounded-2xl bg-blue-600 text-white font-bangla text-sm font-semibold hover:bg-blue-500 transition-colors shadow-lg shadow-blue-500/10 cursor-pointer border-none"
            >
              <RotateCcw className="w-4 h-4" />
              পুনরায় পরীক্ষা দিন
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Active question pagination variables
  const hasQuestions = activeQuestions.length > 0;
  const currentQuestion = hasQuestions ? activeQuestions[activeQuestionIdx] : null;

  return (
    <div className="fixed inset-0 z-50 exam-interface-fullscreen bg-[#ffffff] dark:bg-brand-dark-bg text-gray-900 dark:text-gray-100 font-bangla select-none flex flex-col transition-colors duration-200">
      {/* Top Header Bar */}
      <header className="sticky top-0 z-40 bg-[#ffffff] dark:bg-[#0b0f19] border-b border-slate-200/60 dark:border-slate-800/80 px-3 py-1.5 sm:py-2.5 md:px-8 md:py-3 transition-colors duration-200">
        <div className="relative flex items-center justify-between min-h-[36px]">
          <button
            onClick={onCancel}
            className="flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 rounded-full border border-slate-200/60 dark:border-slate-700/50 text-slate-500 dark:text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
          </button>

          {/* Timer + Global Progress Badges */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-1.5 sm:gap-3">
            <div className="flex items-center gap-1 bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200/60 dark:border-slate-800/50 px-2 sm:px-3 py-0.5 sm:py-1.5 rounded-full transition-colors duration-200">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00CBA9] animate-pulse" />
              <span className="font-english text-[10px] sm:text-sm font-bold text-slate-600 dark:text-slate-300">
                {answeredCount}/{totalQuestionsCount}
              </span>
            </div>

            <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300 bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200/60 dark:border-slate-800/50 px-2 sm:px-3 py-0.5 sm:py-1.5 rounded-full">
              <Clock className="w-3 h-3 text-[#00CBA9]" />
              <span className="font-english text-[10px] sm:text-sm font-bold tabular-nums">
                {formatTime(timeLeft)}
              </span>
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={isFetchingAnswers}
            className="hidden md:inline-block px-4 sm:px-5 py-2 bg-[#00CBA9] hover:bg-[#00b898] active:scale-[0.98] transition-all text-white text-xs sm:text-sm font-bold font-bangla rounded-xl shadow-lg shadow-[#00CBA9]/10 cursor-pointer border-none disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isFetchingAnswers ? 'জমা হচ্ছে...' : 'জমা দিন'}
          </button>
        </div>
      </header>

      {/* Sub Header: Subject Navigation Pills */}
      {subjectKeys.length > 1 && (
        <div className="bg-[#ffffff] dark:bg-brand-dark-bg border-b border-slate-200/60 dark:border-slate-800/40 py-3 px-4 overflow-x-auto transition-colors duration-200 flex-shrink-0">
          <div className="max-w-4xl mx-auto flex items-center gap-2">
            {subjectKeys.map((subjId) => {
              const isActive = activeSubject === subjId;
              const count = (localQuestionsBySubject[subjId] || []).length;
              const answeredInSubject = (localQuestionsBySubject[subjId] || []).filter(
                (q) => answers[q.id] !== undefined
              ).length;

              return (
                <button
                  key={subjId}
                  onClick={() => {
                    scrollToSubject(subjId);
                  }}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 cursor-pointer border ${
                    isActive
                      ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-300 shadow-sm'
                      : 'bg-[#f1f5f9] dark:bg-[#1E293B] border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-850 hover:bg-slate-200'
                  }`}
                >
                  <span>{getSubjectName(subjId)} ({count})</span>
                  {answeredInSubject > 0 && (
                    <span className="bg-slate-200 dark:bg-slate-700 text-[10px] text-slate-700 dark:text-slate-200 px-1.5 py-0.5 rounded-full font-english">
                      {answeredInSubject}/{count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Main active question view */}
      <main 
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 py-4 sm:py-6 px-3 sm:px-4 md:px-8 max-w-4xl w-full mx-auto overflow-y-auto flex flex-col justify-between"
      >
        <div className="space-y-6 w-full">
          {/* Diagnostic status block */}
          <div className="bg-[#ffffff] dark:bg-[#1E1E1E]/60 border border-gray-100 dark:border-white/5 rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.02)] flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center shadow-lg shadow-amber-500/10 flex-shrink-0">
                <Zap className="w-4 h-4 stroke-[2.5]" />
              </div>
              <div className="text-left">
                <h2 className="font-bangla text-xs sm:text-sm font-bold text-[#1F1F1F] dark:text-[#E8EAED]">{title}</h2>
                <p className="font-bangla text-[10px] text-gray-400 dark:text-[#A0A5AD] mt-0.5">
                  {chapterName || getSubjectName(activeSubject)} ({toBengaliNumber(totalQuestionsCount)}টি প্রশ্ন)
                </p>
              </div>
            </div>
            
            <div className="bg-amber-500/10 border border-amber-500/20 px-2.5 py-1 rounded-full text-amber-600 dark:text-amber-400 font-bangla text-[10px] sm:text-xs font-bold flex-shrink-0">
              <span>দাগানো হয়েছে: {toBengaliNumber(answeredCount)} / {toBengaliNumber(totalQuestionsCount)}</span>
            </div>
          </div>

          {/* Active Question Render (DOM contains all question cards grouped by subject) */}
          {totalQuestionsCount === 0 ? (
            <div className="py-20 text-center bg-[#f8fafc] dark:bg-brand-dark-surface border border-slate-200 dark:border-slate-700/20 rounded-3xl">
              <p className="text-slate-550 dark:text-slate-400">এই বিষয়ের জন্য কোনো প্রশ্ন পাওয়া যায়নি।</p>
            </div>
          ) : (
            <div className="space-y-12">
              {subjectKeys.map((subjId) => {
                const questions = localQuestionsBySubject[subjId] || [];
                if (questions.length === 0) return null;

                return (
                  <div 
                    id={`subject-sec-${subjId}`} 
                    key={subjId} 
                    className="space-y-6 scroll-mt-24"
                  >
                    {/* Subject section container without sticky header */}

                    <div className="space-y-6">
                      {questions.map((question, idx) => (
                        <div key={question.id} className="w-full transition-all duration-300">
                          {question.type === 'cq' || questionType === 'cq' ? (
                            <CQQuestionItem 
                              question={question}
                              index={idx}
                              expandedStates={expandedSubQuestions}
                              onToggleSubQuestion={toggleSubQuestion}
                              toBengaliNumber={toBengaliNumber}
                              getQuestionReference={getQuestionReference}
                            />
                          ) : (
                            <MCQQuestionItem 
                              question={question}
                              index={idx}
                              selectedOption={answers[question.id]}
                              onOptionSelect={handleOptionSelect}
                              isFavorite={isFavorite(question.id)}
                              onToggleFavorite={(q) => toggleFavorite({
                                id: q.id,
                                question_text: q.question_text,
                                options: q.options,
                                correct_answer: q.correct_answer,
                                explanation: q.explanation,
                                stem: q.stem,
                                reference: q.reference,
                                subject_id: subjId,
                                subject_name: subjectsData.find(s => s.id === subjId)?.name
                              })}
                              toBengaliNumber={toBengaliNumber}
                              getQuestionReference={getQuestionReference}
                              isFlagged={flaggedQuestionIds.has(question.id)}
                              onReport={(qId) => (window as any).showReportModal?.(qId)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Sticky Bottom Submit Bar — Apple-style (mobile only) */}
      <footer className="md:hidden bg-[#ffffff]/95 dark:bg-[#0b0f19]/95 backdrop-blur-xl border-t border-gray-100 dark:border-white/5 py-3 px-5 z-40 safe-area-inset-bottom">
        <button
          onClick={handleSubmit}
          disabled={isFetchingAnswers}
          className="w-full py-3.5 bg-[#00CBA9] hover:bg-[#00b898] active:scale-[0.98] transition-all text-white font-bangla text-sm font-bold rounded-2xl shadow-lg shadow-[#00CBA9]/25 cursor-pointer text-center border-none tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFetchingAnswers ? 'সাবমিট হচ্ছে...' : 'সাবমিট করুন'}
        </button>
      </footer>
    </div>
  );
}
